import { useEffect, useState } from "react";
import { useLiveQuery } from "dexie-react-hooks";
import {
  CloudOff,
  ReceiptText,
  RotateCcw,
} from "lucide-react";
import { Link } from "react-router-dom";
import {
  Card,
  EmptyState,
  InlineNotice,
  Money,
  PageHeader,
  StatusBadge,
} from "../components/ui";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { db } from "../lib/db";
import { formatDateTime } from "../lib/format";

interface SaleHistoryItem {
  id: string;
  code: string;
  status:
    | "COMPLETED"
    | "PARTIALLY_REFUNDED"
    | "REFUNDED"
    | "VOIDED";
  total: number;
  paymentMethod:
    | "CASH"
    | "BANK_TRANSFER"
    | "DEBT";
  createdAt: string;
  items: Array<{
    id: string;
    productNameSnapshot: string;
    unitSnapshot: string;
    quantity: number;
    baseQuantity: number;
    unitPrice: number;
    lineTotal: number;
  }>;
  returns: Array<{
    id: string;
    code: string;
    total: number;
    createdAt: string;
  }>;
}

const statusLabel: Record<
  SaleHistoryItem["status"],
  string
> = {
  COMPLETED: "Hoàn tất",
  PARTIALLY_REFUNDED: "Hoàn một phần",
  REFUNDED: "Đã hoàn toàn bộ",
  VOIDED: "Đã hủy",
};

const paymentLabel: Record<
  SaleHistoryItem["paymentMethod"],
  string
> = {
  CASH: "Tiền mặt",
  BANK_TRANSFER: "Chuyển khoản",
  DEBT: "Ghi nợ",
};

export function SalesHistoryPage() {
  const auth = useAuth();
  const cached = useLiveQuery(
    async () =>
      ((await db.meta.get("sales-history"))
        ?.value as
        | SaleHistoryItem[]
        | undefined) ?? [],
    [],
    [],
  );
  const localSales = useLiveQuery(
    () =>
      db.sales
        .orderBy("createdAt")
        .reverse()
        .toArray(),
    [],
    [],
  );
  const [loading, setLoading] =
    useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (auth.demo || !navigator.onLine) {
      return;
    }

    setLoading(true);
    setError("");

    void api<SaleHistoryItem[]>("/sales")
      .then(async (sales) => {
        const normalized = sales.map(
          (sale) => ({
            ...sale,
            total: Number(sale.total),
            items: sale.items.map(
              (item) => ({
                ...item,
                quantity: Number(
                  item.quantity,
                ),
                baseQuantity: Number(
                  item.baseQuantity,
                ),
                unitPrice: Number(
                  item.unitPrice,
                ),
                lineTotal: Number(
                  item.lineTotal,
                ),
              }),
            ),
            returns: sale.returns.map(
              (returned) => ({
                ...returned,
                total: Number(
                  returned.total,
                ),
              }),
            ),
          }),
        );

        await db.meta.put({
          key: "sales-history",
          value: normalized,
        });
      })
      .catch((err) =>
        setError(
          err instanceof Error
            ? err.message
            : "Không thể tải lịch sử bán hàng",
        ),
      )
      .finally(() => setLoading(false));
  }, [auth.demo]);

  const sales = auth.demo
    ? localSales.map((sale) => ({
        id: sale.id,
        code: sale.code,
        status:
          "COMPLETED" as const,
        total: sale.total,
        paymentMethod:
          sale.paymentMethod,
        createdAt: sale.createdAt,
        items: [],
        returns: [],
      }))
    : cached;

  return (
    <div className="page-stack">
      <PageHeader
        title="Lịch sử bán hàng"
        description="Đơn đã thanh toán không sửa trực tiếp; mọi hoàn hàng đều tạo giao dịch đảo"
        back
      />

      {!navigator.onLine &&
      !auth.demo ? (
        <InlineNotice
          tone="warning"
          title="Đang ngoại tuyến"
        >
          <span>
            <CloudOff size={16} /> Đang xem
            lịch sử đã lưu gần nhất. Hoàn hàng
            cần kết nối mạng.
          </span>
        </InlineNotice>
      ) : null}

      {error ? (
        <InlineNotice
          tone="danger"
          title={error}
        />
      ) : null}

      <div className="section-title">
        <div>
          <ReceiptText size={20} />
          <h2>Đơn gần đây</h2>
        </div>
        {loading ? (
          <span>Đang cập nhật...</span>
        ) : null}
      </div>

      {sales.length === 0 ? (
        <EmptyState
          title="Chưa có đơn bán"
          description="Các đơn hoàn tất sẽ xuất hiện tại đây."
          action={
            <Link
              to="/sales"
              className="btn btn-primary"
            >
              Bán đơn đầu tiên
            </Link>
          }
        />
      ) : (
        <Card className="sales-history-list">
          {sales.map((sale) => {
            const returnedTotal =
              sale.returns.reduce(
                (sum, returned) =>
                  sum + returned.total,
                0,
              );
            const tone =
              sale.status === "COMPLETED"
                ? "success"
                : sale.status ===
                    "PARTIALLY_REFUNDED"
                  ? "warning"
                  : "neutral";

            const content = (
              <>
                <span className="sale-history-icon">
                  {sale.returns.length >
                  0 ? (
                    <RotateCcw size={19} />
                  ) : (
                    <ReceiptText size={19} />
                  )}
                </span>
                <div>
                  <strong>{sale.code}</strong>
                  <small>
                    {formatDateTime(
                      sale.createdAt,
                    )}{" "}
                    ·{" "}
                    {
                      paymentLabel[
                        sale.paymentMethod
                      ]
                    }
                  </small>
                  {returnedTotal > 0 ? (
                    <small>
                      Đã hoàn{" "}
                      {returnedTotal.toLocaleString(
                        "vi-VN",
                      )}{" "}
                      ₫
                    </small>
                  ) : null}
                </div>
                <div className="sale-history-amount">
                  <Money
                    value={sale.total}
                  />
                  <StatusBadge tone={tone}>
                    {
                      statusLabel[
                        sale.status
                      ]
                    }
                  </StatusBadge>
                </div>
              </>
            );

            return auth.demo ? (
              <div
                className="sale-history-row"
                key={sale.id}
              >
                {content}
              </div>
            ) : (
              <Link
                className="sale-history-row"
                key={sale.id}
                to={`/sales/${sale.id}`}
              >
                {content}
              </Link>
            );
          })}
        </Card>
      )}
    </div>
  );
}
